import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CreditCard, CheckCircle2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

export default function PayPage() {
  const { payToken } = useParams();
  const [params, setParams] = useSearchParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [checking, setChecking] = useState(!!params.get("session_id"));

  const load = () => axios.get(`${API}/payments/public/${payToken}`).then(r => setInv(r.data)).catch(e => setError(e.response?.data?.detail || "Payment link not found"));
  useEffect(() => { load(); }, [payToken]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sid}`);
        if (r.data.payment_status === "paid") { toast.success("Payment received — thank you!"); setChecking(false); setParams({}); load(); return; }
        if (["expired", "failed"].includes(r.data.payment_status)) { toast.error("Payment was not completed"); setChecking(false); return; }
      } catch { /* keep polling */ }
      if (++tries < 8) setTimeout(poll, 2000); else { setChecking(false); toast.info("Still confirming your payment — refresh in a moment"); }
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async (m) => {
    setBusy(m.milestone_id);
    try {
      const r = await axios.post(`${API}/payments/checkout`, { pay_token: payToken, milestone_id: m.milestone_id, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); setBusy(null); }
  };

  if (error) return <Shell><p className="text-center text-red-600 py-20" data-testid="pay-error">{error}</p></Shell>;
  if (!inv) return <Shell><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div></Shell>;

  return (
    <Shell company={inv.company}>
      <h1 className="text-2xl font-bold text-slate-900 text-center" data-testid="pay-title">Invoice {inv.invoice_number}</h1>
      <p className="text-center text-slate-600 text-sm mb-6">{inv.customer_name}</p>
      {checking && <p className="text-center text-sm text-indigo-700 bg-indigo-50 rounded-md p-3 mb-4 flex items-center justify-center gap-2" data-testid="pay-checking"><Loader2 className="w-4 h-4 animate-spin" /> Confirming your payment…</p>}
      {params.get("cancelled") && <p className="text-center text-sm text-slate-600 bg-slate-100 rounded-md p-3 mb-4">Payment cancelled — no charge was made.</p>}
      <div className="grid grid-cols-3 gap-3 mb-5 text-center">
        <Stat label="Total" value={money(inv.total)} /><Stat label="Paid" value={money(inv.amount_paid)} className="text-green-600" /><Stat label="Balance" value={money(inv.balance_due)} className="text-amber-600" testId="pay-balance" />
      </div>
      <Card><CardContent className="pt-4 divide-y">
        {inv.payment_schedule.map((m, i) => (
          <div key={m.milestone_id} className="flex items-center justify-between py-3 gap-3" data-testid={`pay-ms-${i}`}>
            <div><p className="text-sm font-medium text-slate-900">{m.label}</p><p className="text-xs text-slate-500">{m.status === "paid" ? `Paid ${m.paid_at ? new Date(m.paid_at).toLocaleDateString() : ""}` : m.due_date ? `Due ${new Date(m.due_date).toLocaleDateString()}` : "Upcoming"}</p></div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="font-semibold">{money(m.amount)}</span>
              {m.status === "paid" ? <CheckCircle2 className="w-5 h-5 text-green-600" /> : inv.stripe_enabled ? (
                <Button size="sm" onClick={() => pay(m)} disabled={!!busy} data-testid={`pay-btn-${i}`}>{busy === m.milestone_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1" /> Pay</>}</Button>
              ) : <Clock className="w-5 h-5 text-slate-400" />}
            </div>
          </div>
        ))}
      </CardContent></Card>
      {inv.payment_instructions && <Card className="mt-4"><CardContent className="pt-4 text-sm text-slate-700 whitespace-pre-line" data-testid="pay-instructions"><b>Other ways to pay</b><br />{inv.payment_instructions}</CardContent></Card>}
      <p className="text-center text-xs text-slate-400 mt-6">Card payments are processed securely by Stripe. Amounts include tax as itemized on your invoice.</p>
    </Shell>
  );
}

const Stat = ({ label, value, className = "text-slate-900", testId }) => <Card data-testid={testId}><CardContent className="pt-4 pb-3"><p className={`text-lg font-bold ${className}`}>{value}</p><p className="text-xs text-slate-500">{label}</p></CardContent></Card>;
const Shell = ({ company, children }) => <div className="min-h-screen bg-slate-100 py-8 px-4"><div className="max-w-xl mx-auto">{company && <p className="text-center text-xs uppercase tracking-widest text-slate-500 mb-3">{company}</p>}{children}</div></div>;
